import {
  GroupedPromoDiscountItemSchema,
  GroupedPromoFreeGoodItemSchema,
  PromoCrud,
  PromoCrudSchema,
} from '@diamond/shared/schema/promo';
import { Static } from '@sinclair/typebox';

import {
  CartTotalResponseBundledDiscount,
  CartTotalResponseBundledFreeGoods,
  CartTotalResponseCompiledProductDiscount,
  CartTotalResponseSingleDiscount,
  CartTotalResponseSingleFreeGood,
} from './cart';
import { SelectOption } from './common';

export type BackofficePromoResponse = PromoCrud['getManyPromo']['response'];
export type BackofficePromoQuery = PromoCrud['getManyPromo']['query']['status'];
export type Promos = PromoCrud['getManyPromoBuyer']['response'];
export type PromoDetail = PromoCrud['getDetailPromo']['response'];
export type PromoHighlights = PromoCrud['getPromoHighlights']['response'];
export type PromoCount = {
  total_promo_khusus: number;
  total_promo_others: number;
};

export type PromoGroupCount = PromoCrud['getCounterGroupPromo']['response'];
export type PromoGroupHighlights =
  PromoCrud['getPromoHighlightsGrouped']['response'];

export type BackofficeDetailPromoManagement =
  PromoCrud['getByIdBackoffice']['response'];
export type BackofficeDetailPromoItemWithDiscount = Static<
  typeof GroupedPromoDiscountItemSchema
>;
export type BackofficeDetailPromoItemWithFreeGoods = Static<
  typeof GroupedPromoFreeGoodItemSchema
>;

export type BackofficePromoUpdateRequest = PromoCrud['updatePromo']['body'];

export type BackofficePromoBannerUploadResponse =
  PromoCrud['uploadBanner']['response'];

export type BackofficePromoPeriods = '1' | '3' | '6' | 'custom_date';

// Type for Promo List Group
export type BackofficeGroupPromoListQuery = Static<
  typeof PromoCrudSchema.getManyPromo.query
>;
export type BackofficeGroupPromoListResponse = Static<
  typeof PromoCrudSchema.getManyPromo.response
>;
export type BackofficeGroupPromoListItem =
  BackofficeGroupPromoListResponse['data'][number];
export type BackofficePromoStatus = BackofficeGroupPromoListQuery['status'];
export type BackofficePromoType =
  BackofficeGroupPromoListQuery['promo_type_by_shipto'];
export type BackofficePromoMechanism =
  BackofficeGroupPromoListQuery['mechanism'];
export type BackofficePromoCustomerType =
  BackofficeGroupPromoListQuery['customer_type'];

export const sectionToCustomerTypeCode: Record<
  string,
  BackofficePromoCustomerType | 'combination'
> = {
  'food-service': '01',
  'mitra-diamond': '10',
  gabungan: undefined,
};

export type BackofficeUpdateProductHighlightNumberBody =
  PromoCrud['updateProductHighlightNumber']['body'];
export type BackofficeUpdateProductHighlightNumberResponse =
  PromoCrud['updateProductHighlightNumber']['response'];

export interface PromoFilterParams {
  mechanism: Array<SelectOption>;
  promoType: string;
  statusPromo: string;
}

export type BackofficeGetGroupPromoDetailsResponse =
  PromoCrud['getByIdBackoffice']['response'];
export type BackofficePromoCounter =
  PromoCrud['getPromoCounterAdmin']['response'];

export type BackofficeGetGroupedPromosProductHighlightResponse =
  PromoCrud['getGroupedPromosProductHighlight']['response'];

export type BackofficeReorderProductHighlightParams =
  PromoCrud['reorderProductHighlight']['params'];
export type BackofficeReorderProductHighlightBody =
  PromoCrud['reorderProductHighlight']['body'];
export type BackofficeReorderProductHighlightResponse =
  PromoCrud['reorderProductHighlight']['response'];

export type BackofficeGetGroupPromoShiptoList = Static<
  typeof PromoCrudSchema.downloadPromoShiptoExcel.response
>;
export type BackofficeGetGroupPromoTableMechanismParams =
  PromoCrud['getGroupedPromosTableMechanism']['params'];
export type BackofficeGetGroupPromoTableMechanismResponse =
  PromoCrud['getGroupedPromosTableMechanism']['response'];
export type GroupedPromoItem =
  | BackofficeDetailPromoItemWithFreeGoods
  | BackofficeDetailPromoItemWithDiscount;

export type BackofficeReorderPromoParams = PromoCrud['reorderPromo']['params'];
export type BackofficeReorderPromoBody = PromoCrud['reorderPromo']['body'];

export type BackofficeGetPromoCounterAdminQuery =
  PromoCrud['getPromoCounterAdmin']['query'];

export interface FreeGoodsCalculationParams {
  userQuantity: number;
  minimumOrderQuantity: number;
  quantityFreeGoods: number;
  isAppliesInMultiples: boolean;
}

export interface FreeGoodsCalculationResult {
  multiplier: number;
  calculatedFreeQty: number;
  totalFreeQtyUI: number;
}

export type FreeGoodsPromoWithCalculated = CartTotalResponseSingleFreeGood &
  FreeGoodsCalculationResult;
export type BundledFreeGoodsPromoWithCalculated =
  CartTotalResponseBundledFreeGoods & FreeGoodsCalculationResult;
export type DiscountPromoWithCalculated = CartTotalResponseSingleDiscount;
export type BundledDiscountPromoWithCalculated =
  CartTotalResponseBundledDiscount;

export type FreeGoodsPromoRecord = Record<
  string,
  Array<FreeGoodsPromoWithCalculated | BundledFreeGoodsPromoWithCalculated>
>;
export type DiscountPromoRecord = Record<
  string,
  Array<DiscountPromoWithCalculated | BundledDiscountPromoWithCalculated>
>;
export type PromoRecord = Record<
  string,
  CartTotalResponseCompiledProductDiscount
>;
export type OriginalFreeGoodsPromoRecord = Record<
  string,
  Array<
    | Omit<FreeGoodsPromoWithCalculated, keyof FreeGoodsCalculationResult>
    | Omit<
        BundledFreeGoodsPromoWithCalculated,
        keyof FreeGoodsCalculationResult
      >
  >
>;
